import React, { useState, useEffect } from "react";
import ApiService from "../services/ApiService";
import "./CommunityManagement.css";

const CommunityManagement = () => {
  const [activeTab, setActiveTab] = useState("residencies");
  const [residencies, setResidencies] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [flats, setFlats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Form states
  const [showResidencyForm, setShowResidencyForm] = useState(false);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [showFlatForm, setShowFlatForm] = useState(false);
  const [selectedResidency, setSelectedResidency] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);
  // Form data
  const [residencyForm, setResidencyForm] = useState({ name: "", image: null });
  const [residencyImagePreview, setResidencyImagePreview] = useState(null);
  const [blockForm, setBlockForm] = useState({ name: "", residencyId: "" });
  const [flatForm, setFlatForm] = useState({ name: "", blockId: "" });
  useEffect(() => {
    loadResidencies();
    loadBlocks(); // Load all blocks for dropdowns
  }, []);

  const loadResidencies = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getResidencies();
      setResidencies(response.data || []);
    } catch (error) {
      setError("Failed to load residencies");
      console.error("Error loading residencies:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadBlocks = async (residencyId = null) => {
    try {
      setLoading(true);
      const response = await ApiService.getBlocks(residencyId);
      setBlocks(response.data || []);
    } catch (error) {
      setError("Failed to load blocks");
      console.error("Error loading blocks:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadFlats = async (blockId = null) => {
    try {
      setLoading(true);
      const response = await ApiService.getFlats(blockId);
      setFlats(response.data || []);
    } catch (error) {
      setError("Failed to load flats");
      console.error("Error loading flats:", error);
    } finally {
      setLoading(false);
    }
  };

  // File handling functions
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setResidencyForm({
        ...residencyForm,
        image: file,
      });

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setResidencyImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setResidencyForm({
        ...residencyForm,
        image: null,
      });
      setResidencyImagePreview(null);
    }
  };

  const handleCreateResidency = async (e) => {
    e.preventDefault();
    if (!residencyForm.name.trim()) return;

    try {
      setLoading(true);
      await ApiService.createResidency(residencyForm);
      setSuccess("Residency created successfully!");
      setResidencyForm({ name: "", image: null });
      setResidencyImagePreview(null);
      setShowResidencyForm(false);
      loadResidencies();
    } catch (error) {
      setError("Failed to create residency");
      console.error("Error creating residency:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleCreateBlock = async (e) => {
    e.preventDefault();
    if (!blockForm.name.trim() || !blockForm.residencyId) return;

    try {
      setLoading(true);
      await ApiService.createBlock({
        name: blockForm.name,
        residencyId: parseInt(blockForm.residencyId),
      });
      setSuccess("Block created successfully!");
      setBlockForm({ name: "", residencyId: "" });
      setShowBlockForm(false);

      // Refresh all relevant data
      loadResidencies(); // Update residency block counts
      loadBlocks(); // Refresh all blocks for dropdowns

      if (selectedResidency) {
        loadBlocks(selectedResidency.id); // Refresh filtered blocks if viewing specific residency
      }
    } catch (error) {
      setError("Failed to create block");
      console.error("Error creating block:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleCreateFlat = async (e) => {
    e.preventDefault();
    if (!flatForm.name.trim() || !flatForm.blockId) return;

    try {
      setLoading(true);
      await ApiService.createFlat({
        name: flatForm.name,
        blockId: parseInt(flatForm.blockId),
      });
      setSuccess("Flat created successfully!");
      setFlatForm({ name: "", blockId: "" });
      setShowFlatForm(false);

      // Refresh all relevant data
      loadResidencies(); // Update residency counts
      loadBlocks(); // Update block flat counts
      loadFlats(); // Refresh all flats

      if (selectedBlock) {
        loadFlats(selectedBlock.id); // Refresh filtered flats if viewing specific block
      }
    } catch (error) {
      setError("Failed to create flat");
      console.error("Error creating flat:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteResidency = async (id) => {
    if (!window.confirm("Are you sure you want to delete this residency?"))
      return;

    try {
      setLoading(true);
      await ApiService.deleteResidency(id);
      setSuccess("Residency deleted successfully!");
      loadResidencies();
      loadBlocks(); // Refresh blocks as they might be affected
    } catch (error) {
      setError("Failed to delete residency");
      console.error("Error deleting residency:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteBlock = async (id) => {
    if (!window.confirm("Are you sure you want to delete this block?")) return;

    try {
      setLoading(true);
      await ApiService.deleteBlock(id);
      setSuccess("Block deleted successfully!");

      // Refresh all relevant data
      loadResidencies(); // Update residency block counts
      loadBlocks(); // Refresh all blocks for dropdowns

      if (selectedResidency) {
        loadBlocks(selectedResidency.id); // Refresh filtered blocks if viewing specific residency
      }
    } catch (error) {
      setError("Failed to delete block");
      console.error("Error deleting block:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteFlat = async (id) => {
    if (!window.confirm("Are you sure you want to delete this flat?")) return;

    try {
      setLoading(true);
      await ApiService.deleteFlat(id);
      setSuccess("Flat deleted successfully!");

      // Refresh all relevant data
      loadResidencies(); // Update residency counts
      loadBlocks(); // Update block flat counts
      loadFlats(); // Refresh all flats

      if (selectedBlock) {
        loadFlats(selectedBlock.id); // Refresh filtered flats if viewing specific block
      }
    } catch (error) {
      setError("Failed to delete flat");
      console.error("Error deleting flat:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };
  return (
    <div className="community-management dark-theme">
      <div className="community-header">
        <h2>Community Management</h2>
        <p>Manage residencies, blocks, and flats</p>
      </div>

      {/* Messages */}
      {error && (
        <div className="message error-message">
          {error}
          <button onClick={clearMessages} className="close-btn">
            &times;
          </button>
        </div>
      )}
      {success && (
        <div className="message success-message">
          {success}
          <button onClick={clearMessages} className="close-btn">
            &times;
          </button>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="community-tabs">
        <button
          className={`tab-btn ${activeTab === "residencies" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("residencies");
            setSelectedResidency(null);
            setSelectedBlock(null);
          }}
        >
          Residencies
        </button>
        <button
          className={`tab-btn ${activeTab === "blocks" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("blocks");
            setSelectedBlock(null);
            loadBlocks();
          }}
        >
          Blocks
        </button>
        <button
          className={`tab-btn ${activeTab === "flats" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("flats");
            loadFlats();
          }}
        >
          Flats
        </button>
      </div>

      {/* Content based on active tab */}
      <div className="community-content">
        {activeTab === "residencies" && (
          <div className="residencies-section">
            <div className="section-header">
              <h3>Residencies</h3>
              <button
                className="add-btn"
                onClick={() => setShowResidencyForm(true)}
              >
                + Add Residency
              </button>
            </div>

            {/* Residency Form */}
            {showResidencyForm && (
              <div className="form-modal">
                <div className="form-container">
                  <h4>Add New Residency</h4>
                  <form onSubmit={handleCreateResidency}>
                    <div className="form-group">
                      <label>Residency Name *</label>
                      <input
                        type="text"
                        value={residencyForm.name}
                        onChange={(e) =>
                          setResidencyForm({
                            ...residencyForm,
                            name: e.target.value,
                          })
                        }
                        placeholder="Enter residency name"
                        required
                      />{" "}
                    </div>
                    <div className="form-group">
                      <label>Image Upload (optional)</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                      {residencyImagePreview && (
                        <div className="image-preview">
                          <img
                            src={residencyImagePreview}
                            alt="Preview"
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                              marginTop: "10px",
                              borderRadius: "4px",
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="form-actions">
                      <button type="submit" disabled={loading}>
                        {loading ? "Creating..." : "Create Residency"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowResidencyForm(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Residencies List */}
            <div className="items-grid">
              {residencies.map((residency) => (
                <div key={residency.id} className="item-card">
                  {" "}
                  {residency.image && (
                    <div className="item-image">
                      <img
                        src={ApiService.getImageUrl(residency.image)}
                        alt={residency.name}
                      />
                    </div>
                  )}
                  <div className="item-content">
                    <h4>{residency.name}</h4>
                    <p className="item-stats">
                      {residency.blocks?.length || 0} blocks
                    </p>
                    <div className="item-actions">
                      <button
                        className="view-btn"
                        onClick={() => {
                          setSelectedResidency(residency);
                          setActiveTab("blocks");
                          loadBlocks(residency.id);
                        }}
                      >
                        View Blocks
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteResidency(residency.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "blocks" && (
          <div className="blocks-section">
            <div className="section-header">
              <h3>
                Blocks
                {selectedResidency && (
                  <span className="breadcrumb">
                    {" "}
                    in {selectedResidency.name}
                  </span>
                )}
              </h3>
              <button
                className="add-btn"
                onClick={() => setShowBlockForm(true)}
              >
                + Add Block
              </button>
            </div>

            {/* Block Form */}
            {showBlockForm && (
              <div className="form-modal">
                <div className="form-container">
                  <h4>Add New Block</h4>
                  <form onSubmit={handleCreateBlock}>
                    <div className="form-group">
                      <label>Block Name *</label>
                      <input
                        type="text"
                        value={blockForm.name}
                        onChange={(e) =>
                          setBlockForm({
                            ...blockForm,
                            name: e.target.value,
                          })
                        }
                        placeholder="Enter block name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Residency *</label>
                      <select
                        value={blockForm.residencyId}
                        onChange={(e) =>
                          setBlockForm({
                            ...blockForm,
                            residencyId: e.target.value,
                          })
                        }
                        required
                      >
                        <option value="">Select Residency</option>
                        {residencies.map((residency) => (
                          <option key={residency.id} value={residency.id}>
                            {residency.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-actions">
                      <button type="submit" disabled={loading}>
                        {loading ? "Creating..." : "Create Block"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowBlockForm(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Blocks List */}
            <div className="items-grid">
              {blocks.map((block) => (
                <div key={block.id} className="item-card">
                  <div className="item-content">
                    <h4>{block.name}</h4>
                    <p className="item-meta">
                      Residency: {block.residency?.name}
                    </p>
                    <p className="item-stats">
                      {block.flats?.length || 0} flats
                    </p>
                    <div className="item-actions">
                      <button
                        className="view-btn"
                        onClick={() => {
                          setSelectedBlock(block);
                          setActiveTab("flats");
                          loadFlats(block.id);
                        }}
                      >
                        View Flats
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteBlock(block.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "flats" && (
          <div className="flats-section">
            <div className="section-header">
              <h3>
                Flats
                {selectedBlock && (
                  <span className="breadcrumb">
                    {" "}
                    in {selectedBlock.name}
                    {selectedBlock.residency && (
                      <span>, {selectedBlock.residency.name}</span>
                    )}
                  </span>
                )}
              </h3>
              <button className="add-btn" onClick={() => setShowFlatForm(true)}>
                + Add Flat
              </button>
            </div>

            {/* Flat Form */}
            {showFlatForm && (
              <div className="form-modal">
                <div className="form-container">
                  <h4>Add New Flat</h4>
                  <form onSubmit={handleCreateFlat}>
                    <div className="form-group">
                      <label>Flat Name *</label>
                      <input
                        type="text"
                        value={flatForm.name}
                        onChange={(e) =>
                          setFlatForm({
                            ...flatForm,
                            name: e.target.value,
                          })
                        }
                        placeholder="Enter flat name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Block *</label>
                      <select
                        value={flatForm.blockId}
                        onChange={(e) =>
                          setFlatForm({
                            ...flatForm,
                            blockId: e.target.value,
                          })
                        }
                        required
                      >
                        <option value="">Select Block</option>
                        {blocks.map((block) => (
                          <option key={block.id} value={block.id}>
                            {block.name} ({block.residency?.name})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-actions">
                      <button type="submit" disabled={loading}>
                        {loading ? "Creating..." : "Create Flat"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowFlatForm(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Flats List */}
            <div className="items-grid">
              {flats.map((flat) => (
                <div key={flat.id} className="item-card">
                  <div className="item-content">
                    <h4>{flat.name}</h4>
                    <p className="item-meta">Block: {flat.block?.name}</p>
                    <p className="item-meta">
                      Residency: {flat.block?.residency?.name}
                    </p>
                    <div className="item-actions">
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteFlat(flat.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner">Loading...</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityManagement;
